import React, { useEffect, useState, useRef, useMemo } from 'react'
import { Search, Send, FileText, ImageIcon, ChevronLeft, MoreVertical, Archive, X, Trash2 } from 'lucide-react'
import axios from '../../AxiosConfig'
import config from '../../config'
import { toast } from 'react-toastify'
import jwt_decode from 'jwt-decode'
import { Paperclip } from "lucide-react";
import EmptyState from '../Elements/UI/EmptyState'
import { normalizeUrl } from '../Elements/UI/fileLink'
import ConfirmModal from '../Elements/UI/ConfirmModal'

// The API returns chat users with RoleID (no role name); RoleID 1 = SuperAdmin
const ADMIN_ROLE_ID = '1'
const isAdminUser = u => !!u && (u.Role === 'SuperAdmin' || String(u.RoleID) === ADMIN_ROLE_ID)

export default function Chatscreen() {
  const token = localStorage.getItem('token')
  const [activeTab, setActiveTab] = useState('all')
  const [selectedFile, setSelectedFile] = useState(null);
  let loggedInUserID = null
  let loggedInUserRole = null

  if (token) {
    const tokenData = jwt_decode(token)
    loggedInUserID = tokenData.ID
    loggedInUserRole = isAdminUser(tokenData) ? 'SuperAdmin' : tokenData.Role

    if (loggedInUserRole === 'SuperAdmin') {
      config.superAdminId = loggedInUserID
    }
  }

  const [contacts, setContacts] = useState([])
  const [selectedChatId, setSelectedChatId] = useState(null)
  const [messages, setMessages] = useState({})
  const [messageInput, setMessageInput] = useState('')
  const [search, setSearch] = useState('')

  const messagesEndRef = useRef(null)
  // members auto-create their admin chat only once (prevents a create/fetch loop)
  const createAttempted = useRef(false)
  const supportEnsured = useRef(false)
  // chat waiting for delete confirmation: { id, name }
  const [chatToDelete, setChatToDelete] = useState(null)
  // own message waiting for delete confirmation: { id, chatId }
  const [messageToDelete, setMessageToDelete] = useState(null)
  const fileInputRef = useRef(null)

  const currentMessages = messages[selectedChatId] || []
  const currentContact = contacts.find(c => c.id === selectedChatId)

  // Scroll inside the messages pane only (scrollIntoView could also move the page).
  // Runs when the chat changes or a message is added — not on every re-render,
  // which previously yanked the view down while the user was scrolling.
  const messageCount = currentMessages.length
  const lastChatRef = useRef(null)
  useEffect(() => {
    const box = messagesEndRef.current?.parentElement
    if (!box) return
    const switchedChat = lastChatRef.current !== selectedChatId
    lastChatRef.current = selectedChatId
    const nearBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 120
    if (switchedChat || nearBottom) {
      box.scrollTo({ top: box.scrollHeight, behavior: switchedChat ? 'auto' : 'smooth' })
    }
  }, [selectedChatId, messageCount])

  useEffect(() => {
    if (loggedInUserID) getChats()
  }, [loggedInUserID, activeTab])

  const getChats = async () => {
    try {
      // members: make sure the chat with the admin exists, so the admin shows
      // up on the very first login (the server finds the admin itself)
      if (loggedInUserRole !== 'SuperAdmin' && !supportEnsured.current) {
        supportEnsured.current = true
        try {
          await axios.get(`${config.apiUrl}/chats/support`, { headers: { Authorization: token } })
        } catch (e) {
          console.warn('Support chat lookup failed, falling back', e)
        }
      }

      const res = await axios.get(
        `${config.apiUrl}/chats?IsArchived=${activeTab === 'archived'}`,
        {
          headers: { Authorization: token }
        }
      )

      const chatsArray = res.data.data || []

      let filteredChats = chatsArray
      if (loggedInUserRole !== 'SuperAdmin') {
        // members only chat with the society admin
        filteredChats = chatsArray.filter(
          chat => isAdminUser(chat.ChatUser) || chat.ChatUser?.ID === config.superAdminId
        )
      }

      if (
        filteredChats.length === 0 &&
        loggedInUserRole !== 'SuperAdmin' &&
        activeTab === 'all' &&
        !createAttempted.current
      ) {
        createAttempted.current = true
        await createChatWithSuperAdmin()
        return
      }

      const mapped = filteredChats.map(chat => {
        const chatUser = chat.ChatUser
        if (!chatUser) return null
        return {
          id: chat.ID,
          name: chatUser.Name || (isAdminUser(chatUser) ? 'Society Admin' : 'Member'),
          role: isAdminUser(chatUser) ? 'SuperAdmin' : (chatUser.Role || 'Member'),
          lastMessage: chat.LatestMessage?.Content || 'Start a new conversation',
          lastMessageType: chat.LatestMessage?.Type || 'text',
          time: chat.LatestMessage
            ? new Date(chat.LatestMessage.CreatedOn).toLocaleTimeString()
            : ''
        }
      }).filter(Boolean)

      setContacts(mapped)

      if (mapped.length && !selectedChatId) {
        setSelectedChatId(mapped[0].id)
      }
    } catch (err) {
      console.error(err)
      toast.error('Failed to load chats')
    }
  }

  const deleteChat = async () => {
    if (!chatToDelete) return
    const { id } = chatToDelete
    try {
      await axios.delete(`${config.apiUrl}/chats/${id}`, {
        headers: { Authorization: token }
      })
      setContacts(prev => prev.filter(c => c.id !== id))
      setMessages(prev => {
        const next = { ...prev }
        delete next[id]
        return next
      })
      if (selectedChatId === id) setSelectedChatId(null)
      setChatToDelete(null)
      toast.success('Chat deleted')
      // members always need a support chat: allow a fresh one to be created
      if (loggedInUserRole !== 'SuperAdmin') {
        createAttempted.current = false
        supportEnsured.current = false
      }
      getChats()
    } catch (err) {
      console.error(err)
      toast.error(err.response?.data?.message || 'Failed to delete chat')
    }
  }

  const deleteMessage = async () => {
    if (!messageToDelete) return
    const { id, chatId } = messageToDelete
    try {
      await axios.delete(`${config.apiUrl}/messages/${id}`, {
        headers: { Authorization: token }
      })
      setMessages(prev => ({
        ...prev,
        [chatId]: (prev[chatId] || []).filter(m => m.id !== id)
      }))
      setMessageToDelete(null)
      toast.success('Message deleted')
      getChats() // refresh the chat list preview
    } catch (err) {
      console.error(err)
      toast.error(err.response?.data?.message || 'Failed to delete message')
    }
  }

  const toggleArchiveChat = async (chatId, isArchived) => {
    try {
      await axios.post(
        `${config.apiUrl}/chats/archive`,
        {
          chatId: chatId,
          isArchived: isArchived
        },
        { headers: { Authorization: token } }
      )

      getChats()
    } catch (err) {
      console.error(err)
      toast.error('Archive action failed')
    }
  }

  const createChatWithSuperAdmin = async () => {
    try {
      if (!config.superAdminId) {
        throw new Error('SuperAdmin ID not defined')
      }

      const res = await axios.post(
        `${config.apiUrl}/chats/create`,
        {
          ChatName: null,
          IsGroupChat: false,
          Users: [loggedInUserID, config.superAdminId]
        },
        { headers: { Authorization: token } }
      )

      const newChatId = res.data.ID || res.data.data?.ID
      if (newChatId) setSelectedChatId(newChatId)

      getChats()
    } catch (err) {
      console.error(err)
      toast.error('Unable to start support chat')
    }
  }

  const fetchMessages = async chatId => {
    if (!chatId) return
    try {
      const res = await axios.get(`${config.apiUrl}/messages/${chatId}`, {
        headers: { Authorization: token }
      })

      const msgs = res.data.data || []

      const formatted = msgs.map(msg => ({
        id: msg.ID,
        text: msg.Content,
        type: msg.Type || 'text',
        sent: msg.Sender === loggedInUserID,
        time: new Date(msg.CreatedOn).toLocaleTimeString(),
        createdOn: msg.CreatedOn
      }))

      setMessages(prev => ({
        ...prev,
        [chatId]: formatted
      }))
    } catch (err) {
      console.error(err)
      toast.error('Failed to load messages')
    }
  }

  const handleSend = async () => {

  if (selectedFile) {
    await uploadFile();
  }

  if (messageInput.trim()) {
    await sendMessage();
  }
};


  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedChatId) return
    try {
      await axios.post(
        `${config.apiUrl}/messages/create`,
        {
          ChatId: selectedChatId,
          Type: 'text',
          Content: messageInput
        },
        { headers: { Authorization: token } }
      )
      setMessageInput('')
      fetchMessages(selectedChatId)
      getChats()
    } catch (err) {
      console.error(err)
      toast.error('Message send failed')
    }
  }

  const uploadFile = async () => {
  if (!selectedFile || !selectedChatId) return;

  try {

    // Upload file to your server
    const formData = new FormData();
    formData.append("upload", selectedFile);

    const uploadRes = await axios.post(
    "https://blossom-api.skyq.tech/upload/image",
    formData,
    {
        headers:{
            Authorization: token
        }
    }
);

    // Permanent URL returned by server
    const fileUrl = uploadRes.data.data;

    // Save document (optional)
    await axios.post(
      "https://blossom-api.skyq.tech/post/Document",
      {
        ProjectID: 1,
        Wing: "A",
        Data: {
          Title: selectedFile.name,
          DocumentLink: fileUrl
        }
      },
      {
        headers: { Authorization: token }
      }
    );

    // Send chat message
    await axios.post(
      "https://blossom-api.skyq.tech/messages/create",
      {
        ChatId: selectedChatId,
        Type: selectedFile.type.startsWith("image")
          ? "image"
          : "file",
        Content: fileUrl
      },
      {
        headers: { Authorization: token }
      }
    );

    setSelectedFile(null);
    fileInputRef.current.value = "";

    fetchMessages(selectedChatId);
    getChats();

  } catch (err) {
    console.log(err);
    toast.error("Upload failed");
  }
};

  useEffect(() => {
    if (loggedInUserID) getChats()
  }, [loggedInUserID])

  useEffect(() => {
    if (selectedChatId) fetchMessages(selectedChatId)
  }, [selectedChatId])

  const filteredContacts = contacts.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  )

  // ---- UI-only helpers (no data changes) ----
  const isSuperAdmin = loggedInUserRole === 'SuperAdmin'
  // Phones show one pane at a time; members go straight to their support chat
  const [mobilePane, setMobilePane] = useState(isSuperAdmin ? 'list' : 'chat')
  const [sending, setSending] = useState(false)

  const openChat = id => {
    setSelectedChatId(id)
    setMobilePane('chat')
  }

  const initials = name =>
    (name || '?')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(p => p[0].toUpperCase())
      .join('')

  const formatClock = value => {
    const d = value ? new Date(value) : null
    if (!d || isNaN(d)) return ''
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const dayLabel = value => {
    const d = new Date(value)
    if (isNaN(d)) return ''
    const today = new Date()
    const yesterday = new Date()
    yesterday.setDate(today.getDate() - 1)
    if (d.toDateString() === today.toDateString()) return 'Today'
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  const previewText = chat =>
    chat.lastMessageType === 'image' ? 'Photo'
      : chat.lastMessageType === 'file' ? 'Attachment'
      : chat.lastMessage

  const filePreviewUrl = useMemo(
    () => (selectedFile && selectedFile.type.startsWith('image') ? URL.createObjectURL(selectedFile) : null),
    [selectedFile]
  )
  useEffect(() => () => { if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl) }, [filePreviewUrl])

  const canSend = !!selectedChatId && (!!messageInput.trim() || !!selectedFile) && !sending

  const onSend = async () => {
    if (!canSend) return
    setSending(true)
    try {
      await handleSend()
    } finally {
      setSending(false)
    }
  }

  const clearFile = () => {
    setSelectedFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <div className={`bl-chat ${mobilePane === 'chat' ? 'is-chat-open' : ''}`}>

      {/* ---------- Conversation list ---------- */}
      <aside className="bl-chat__list" aria-label="Conversations">
        <div className="bl-chat__list-head">
          <h2 className="bl-chat__title">Messages</h2>

          <div className="bl-segmented" role="tablist" aria-label="Chat filter">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'all'}
              className={activeTab === 'all' ? 'active' : ''}
              onClick={() => setActiveTab('all')}
            >
              All chats
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'archived'}
              className={activeTab === 'archived' ? 'active' : ''}
              onClick={() => setActiveTab('archived')}
            >
              Archived
            </button>
          </div>

          <div className="bl-search">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              className="form-control"
              placeholder="Search conversations"
              aria-label="Search conversations"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        <ul className="bl-chat__contacts">
          {filteredContacts.map(chat => (
            <li key={chat.id} className={`bl-contact ${selectedChatId === chat.id ? 'active' : ''}`}>
              <button
                type="button"
                className="bl-contact__main"
                onClick={() => openChat(chat.id)}
                aria-current={selectedChatId === chat.id ? 'true' : undefined}
              >
                <span className="bl-avatar bl-avatar--lg" aria-hidden="true">{initials(chat.name)}</span>
                <span className="bl-contact__text">
                  <span className="bl-contact__row">
                    <span className="bl-contact__name">
                      {chat.name}
                      {chat.role === 'SuperAdmin' && (
                        <span className="bl-chip bl-chip--primary ms-2">Admin</span>
                      )}
                    </span>
                    <span className="bl-contact__time">{(chat.time || '').replace(/(\d{1,2}:\d{2}):\d{2}/, '$1')}</span>
                  </span>
                  <span className="bl-contact__preview">
                    {chat.lastMessageType === 'image' && <ImageIcon size={14} aria-hidden="true" />}
                    {chat.lastMessageType === 'file' && <FileText size={14} aria-hidden="true" />}
                    {previewText(chat)}
                  </span>
                </span>
              </button>

              {isSuperAdmin && (
                <div className="dropdown bl-contact__menu">
                  <button
                    type="button"
                    className="btn btn-icon btn-sm"
                    onClick={(e) => e.stopPropagation()}
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    aria-label={`More options for ${chat.name}`}
                  >
                    <MoreVertical size={16} aria-hidden="true" />
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleArchiveChat(chat.id, activeTab === 'all')
                        }}
                      >
                        <Archive size={16} aria-hidden="true" />
                        {activeTab === 'all' ? 'Archive' : 'Unarchive'}
                      </button>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button
                        type="button"
                        className="dropdown-item text-danger"
                        onClick={(e) => {
                          e.stopPropagation()
                          setChatToDelete({ id: chat.id, name: chat.name })
                        }}
                      >
                        <Trash2 size={16} aria-hidden="true" />
                        Delete chat
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </li>
          ))}

          {filteredContacts.length === 0 && (
            <li>
              <EmptyState
                icon="chatbubbles-outline"
                title={search ? 'No matches' : activeTab === 'archived' ? 'No archived chats' : 'No conversations yet'}
                text={search ? 'Try a different name.' : undefined}
              />
            </li>
          )}
        </ul>
      </aside>

      {/* ---------- Conversation ---------- */}
      <section className="bl-chat__pane" aria-label="Conversation">
        <header className="bl-chat__pane-head">
          <button
            type="button"
            className="btn btn-icon d-md-none"
            onClick={() => setMobilePane('list')}
            aria-label="Back to conversations"
          >
            <ChevronLeft size={22} aria-hidden="true" />
          </button>
          <span className="bl-avatar" aria-hidden="true">{initials(currentContact?.name || 'Support')}</span>
          <div className="bl-chat__pane-meta">
            <div className="bl-chat__pane-name">
              {currentContact?.name || 'Support Chat'}
              {currentContact?.role === 'SuperAdmin' && (
                <span className="bl-chip bl-chip--primary ms-2">Admin</span>
              )}
            </div>
            <div className="bl-chat__pane-sub">
              {currentContact?.role === 'SuperAdmin' ? 'Society administration' : 'Member'}
            </div>
          </div>
          {currentContact && (
            <button
              type="button"
              className="btn btn-icon bl-chat__delete ms-auto"
              onClick={() => setChatToDelete({ id: currentContact.id, name: currentContact.name })}
              aria-label={`Delete chat with ${currentContact.name}`}
              title="Delete chat"
            >
              <Trash2 size={18} aria-hidden="true" />
            </button>
          )}
        </header>

        <div className="bl-chat__messages" role="log" aria-live="polite" aria-label="Messages">
          {currentMessages.map((msg, i) => {
            const prev = currentMessages[i - 1]
            const showDay = msg.createdOn &&
              (!prev || !prev.createdOn || new Date(prev.createdOn).toDateString() !== new Date(msg.createdOn).toDateString())
            const grouped = prev && prev.sent === msg.sent && !showDay

            return (
              <React.Fragment key={msg.id}>
                {showDay && (
                  <div className="bl-chat__day"><span>{dayLabel(msg.createdOn)}</span></div>
                )}
                <div className={`bl-msg ${msg.sent ? 'bl-msg--sent' : 'bl-msg--recv'} ${grouped ? 'is-grouped' : ''}`}>
                  {msg.sent && (
                    <button
                      type="button"
                      className="btn btn-icon btn-sm bl-msg__delete"
                      onClick={() => setMessageToDelete({ id: msg.id, chatId: selectedChatId })}
                      aria-label="Delete this message"
                      title="Delete message"
                    >
                      <Trash2 size={15} aria-hidden="true" />
                    </button>
                  )}
                  <div className={`bl-msg__bubble ${msg.type === 'image' ? 'is-media' : ''}`}>
                    {msg.type === 'image' ? (
                      <a href={normalizeUrl(msg.text)} target="_blank" rel="noopener noreferrer" aria-label="Open image in a new tab">
                        <img src={normalizeUrl(msg.text)} alt="Shared attachment" loading="lazy" />
                      </a>
                    ) : msg.type === 'file' && msg.text.endsWith('.pdf') ? (
                      <div className="bl-msg__pdf">
                        <iframe src={normalizeUrl(msg.text)} title={`PDF preview: ${msg.text.split('/').pop()}`}></iframe>
                        <a href={normalizeUrl(msg.text)} target="_blank" rel="noopener noreferrer" className="bl-msg__file">
                          <FileText size={16} aria-hidden="true" />
                          {msg.text.split('/').pop()}
                        </a>
                      </div>
                    ) : msg.type === 'file' || msg.type === 'video' ? (
                      <a href={normalizeUrl(msg.text)} target="_blank" rel="noopener noreferrer" className="bl-msg__file">
                        <FileText size={16} aria-hidden="true" />
                        {msg.text.split('/').pop()}
                      </a>
                    ) : (
                      <p className="bl-msg__text">{msg.text}</p>
                    )}
                    <time className="bl-msg__time" dateTime={msg.createdOn}>
                      {formatClock(msg.createdOn) || msg.time}
                    </time>
                  </div>
                </div>
              </React.Fragment>
            )
          })}
          <div ref={messagesEndRef} />

          {currentMessages.length === 0 && (
            <EmptyState
              icon="chatbubble-ellipses-outline"
              title="No messages yet"
              text="Say hello. Replies will appear here."
            />
          )}
        </div>

        {selectedFile && (
          <div className="bl-chat__attachment">
            {filePreviewUrl ? (
              <img src={filePreviewUrl} alt="" />
            ) : (
              <span className="bl-doc__icon" aria-hidden="true"><FileText size={20} /></span>
            )}
            <span className="bl-chat__attachment-name">{selectedFile.name}</span>
            <button type="button" className="btn btn-icon btn-sm" onClick={clearFile} aria-label="Remove attachment">
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        )}

        <form
          className="bl-chat__composer"
          onSubmit={e => {
            e.preventDefault()
            onSend()
          }}
        >
          <button
            type="button"
            className="btn btn-icon"
            onClick={() => fileInputRef.current?.click()}
            aria-label="Attach a file"
          >
            <Paperclip size={20} aria-hidden="true" />
          </button>
          <input
            type="file"
            ref={fileInputRef}
            className="d-none"
            tabIndex={-1}
            onChange={(e) => {
              if (e.target.files.length) {
                setSelectedFile(e.target.files[0])
              }
            }}
          />
          <input
            className="form-control bl-chat__input"
            placeholder="Type a message"
            aria-label="Message"
            value={messageInput}
            onChange={e => setMessageInput(e.target.value)}
            disabled={!selectedChatId}
          />
          <button
            type="submit"
            className="btn btn-primary bl-chat__send"
            disabled={!canSend}
            aria-label="Send message"
          >
            {sending
              ? <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
              : <Send size={18} aria-hidden="true" />}
          </button>
        </form>
      </section>
      <ConfirmModal
        show={!!messageToDelete}
        onClose={() => setMessageToDelete(null)}
        onConfirm={deleteMessage}
        title="Delete this message?"
        message="It will be removed for both of you. This can't be undone."
        confirmLabel="Delete"
      />
      <ConfirmModal
        show={!!chatToDelete}
        onClose={() => setChatToDelete(null)}
        onConfirm={deleteChat}
        title="Delete this chat?"
        message={chatToDelete
          ? `The conversation with ${chatToDelete.name} will be removed for both of you.`
          : ''}
        confirmLabel="Delete chat"
      />
    </div>
  )
}
